import Button from '../../../../components/form-control/Button';
import { UserGroupIcon, ShieldCheckIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { assignBrokerToPolicy, changePolicyManager } from '../../../../api/requests';
import SelectDropdown, { ISelectDropdownItem } from '../../../../components/form-control/SelectDropdown';
import { getFormattedAccessRight, showToast } from '../../../../utils/miscFunctions';
import { useQuery } from '@tanstack/react-query';
import usePartner from '../../../../hooks/partnerHook';
import { motion } from 'framer-motion';
import { COMPANY_TYPES } from '../../../../resources/enums';

export interface IAssignPolicy {
  policyId: string;
  managerId: string;
}

interface IAssignPolicyProps {
  onProceed: () => void;
  onClose: () => void;
  policy: any;
  actionType: 'assignToManager' | 'assignToBroker';
}

const AssignPolicy = ({ onClose, onProceed, policy, actionType }: IAssignPolicyProps) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const { getAllCompanyStaff, fetchAllCompaniesByType } = usePartner();
  const id = policy?.manager?.company?.id || policy?.broker?.id;
  const isBroker = actionType === 'assignToBroker';

  const { data: admins, isLoading: loadingAdmins } = useQuery({
    queryKey: ['getAllCompanyStaff', id],
    queryFn: () => getAllCompanyStaff(Number(id)),
    enabled: !!id
  })

  const { data: brokers, isLoading: loadingBrokers } = useQuery({
    queryKey: ['fetchAllCompaniesByType', isBroker],
    queryFn: () => fetchAllCompaniesByType(COMPANY_TYPES.BROKER),
    enabled: !!isBroker
  })


  const submitHandler = async (values: any) => {
    setSubmitting(true);

    const payload: IAssignPolicy = {
      policyId: policy?.ref || policy?.id,
      managerId: values.id
    }

    try {

      const response = await (actionType === 'assignToManager' ? changePolicyManager : assignBrokerToPolicy)(payload.policyId, Number(values.id));
      if (response) {
        showToast.success(response?.message || 'Policy assigned successfully',);
        onProceed();
      }
    } catch (error: any) {
      showToast.error(error?.description || 'Failed to assign policy');
    } finally {
      setSubmitting(false);
    }

  };

  const getDropdownLabel = () => {
    switch (actionType) {
      case 'assignToManager':
        return 'Select new manager';
      case 'assignToBroker':
        return 'Select new broker';
      default:
        return 'Select new manager';
    }
  }

  const getModalTitle = () => {
    switch (actionType) {
      case 'assignToManager':
        return 'Assign Policy Manager';
      case 'assignToBroker':
        return 'Assign Policy To Broker';
      default:
        return 'Assign Policy';
    }
  }

  const getModalDescription = () => {
    switch (actionType) {
      case 'assignToManager':
        return 'Select a new manager to oversee this policy. The current manager will be replaced.';
      case 'assignToBroker':
        return 'Select a new broker to handle this policy. The current broker will be replaced.';
      default:
        return 'Select a new person to manage this policy.';
    }
  }

  const getDropdownList = () => {
    switch (actionType) {
      case 'assignToBroker':
        return brokers?.map((broker: any) => ({
          name: broker.name,
          value: broker.id
        })) || [];

      case 'assignToManager':
        return admins?.map((admin: any) => ({
          name: `${admin.fullName} ${admin.role ? `(${getFormattedAccessRight(admin.role)})` : ''}`,
          value: admin.id
        })) || []

      default:
        return []
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="bg-white dark:bg-darkBg rounded-2xl shadow-xl max-w-md w-full mx-auto"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 dark:border-darkBg-border">
        <div className="flex items-center space-x-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isBroker ? 'bg-blue-100' : 'bg-purple-100'
            }`}>
            {isBroker ? (
              <UserGroupIcon className="w-5 h-5 text-blue-600" />
            ) : (
              <ShieldCheckIcon className="w-5 h-5 text-purple-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">
              {getModalTitle()}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Policy #{policy?.ref || 'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 dark:text-darkBg-mainText">
        {/* Warning Message */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5"
        >
          <div className="flex items-start">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Important Notice</p>
              <p className="text-amber-700 mt-1">
                {getModalDescription()}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Current Assignment Info */}
        {(policy?.manager || policy?.broker) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
            className="bg-gray-50 dark:bg-darkBg rounded-lg p-3 mb-5"
          >
            <p className="text-xs font-medium text-gray-500 dark:text-darkBg-mainText uppercase tracking-wider mb-2">
              Current {isBroker ? 'Broker' : 'Manager'}
            </p>
            <p className="text-sm font-medium text-gray-900 dark:text-darkBg-mainText">
              {isBroker ? policy?.broker?.name : policy?.manager?.fullName || 'Not assigned'}
            </p>
          </motion.div>
        )}

        <Formik
          initialValues={{
            id: ''
          }}
          validationSchema={Yup.object({
            id: Yup.mixed().required(`Please select a new ${isBroker ? 'broker' : 'manager'}`)
          })}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={(values) => {
            submitHandler(values);
          }}>
          {({ values, errors, handleSubmit, handleChange, setFieldValue, handleBlur, touched, isSubmitting }) => {
            return (
              <form onSubmit={handleSubmit} className="space-y-4">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.3 }}
                >
                  <SelectDropdown
                    label={getDropdownLabel()}
                    name="id"
                    list={getDropdownList() || []}
                    listSelectedValue={values.id}
                    onChange={(val: ISelectDropdownItem | null) => {
                      setFieldValue('id', val);
                    }}
                    search
                    searchPlaceHolder='Search by name'
                    onBlur={handleBlur}
                    placeholder={getDropdownLabel()}
                    error={touched.id && errors.id ? errors.id : undefined}
                    helperText={touched.id && errors.id ? errors.id : undefined}
                    required
                    disabled={loadingAdmins || loadingBrokers}
                  />
                </motion.div>

                {/* Action Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                  className="flex gap-5 pt-2"
                >
                  <Button
                    type="button"
                    label="Cancel"
                    bgColor="secondary"
                    onClick={onClose}
                    disabled={submitting}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    label={submitting ? `Assigning...` : `Assign ${isBroker ? 'Broker' : 'Manager'}`}
                    bgColor="primary"
                    loading={submitting}
                    disabled={submitting || !values.id || Object.keys(errors).length > 0}
                    fullWidth
                  />
                </motion.div>
              </form>
            )
          }}
        </Formik>
      </div>
    </motion.div>
  );
};

export default AssignPolicy;
